import { compareDue, isDueFilter, todayIn, type DueFilter } from "@boem/core/due";
import { isDraftsFilter, LANES, type BoardItem, type DraftsFilter, type ItemKind, type Lane } from "@boem/core/shared";
import { matchesFilters } from "@boem/core/views";
import { readOrgSetting, writeOrgSetting } from "./org.ts";

export type Sort = "num" | "priority" | "newest" | "attention" | "due";
/** Teams filter: added via Teams, resolved via Teams, or either */
export type TeamsFilter = "in" | "resolved" | "any";
/** Notes: everyone's, only what people wrote themselves, or only what their AI apps wrote */
export type NotesFilter = "all" | "people" | "ai";

/** Written by someone's AI app (a note or a reaction). */
export const byAi = (x: { agent: unknown }) => !!x.agent;
export const hasAi = (it: BoardItem) => it.notes.some(byAi) || it.reactions.some(byAi);

export interface Filters {
  /** which queues to show: to do, PR review (OEMUAP), deploy queue, verify queue, closed (empty = all) */
  lanes: Set<Lane>;
  high: boolean;
  tag: string | null;
  role: string | null;
  /** whose items (member names; "__none" for nobody's), any of them; empty = everyone's. The toolbar's assignee button picks them */
  asg: string[];
  teams: TeamsFilter | null;
  q: string;
  notReviewed: boolean;
  nondev: boolean;
  notes: NotesFilter;
  /** feature / bug / task, or "none" for items not classified yet */
  kind: Exclude<ItemKind, "project"> | "none" | null;
  /** one project's items (the archive, or a project picked on the main board); null = the view's own */
  project: number | null;
  /** only items in no project (OEM-260); never with `project` */
  noProject: boolean;
  /** leave these projects' items out, by their # numbers (OEM-260) */
  hideProjects: number[];
  /** drafts (OEM-286): others' hidden (yours show), everyone's shown too, or only drafts */
  drafts: DraftsFilter;
  /** due dates (OEM-267): overdue, due this week, due later, no date; null = any */
  due: DueFilter | null;
  sort: Sort;
}

/** The queues the board opens on: everything still in progress (closed items are one chip away). */
export const OPEN_LANES: readonly Lane[] = ["todo", "review", "deploy", "verify"];

/** The board opens on everything still in progress - closed items are one chip away. */
export const initialFilters = (): Filters => ({
  lanes: new Set<Lane>(OPEN_LANES),
  high: false,
  tag: null,
  role: null,
  asg: [],
  teams: null,
  q: "",
  notReviewed: false,
  nondev: false,
  notes: "all",
  kind: null,
  project: null,
  noProject: false,
  hideProjects: [],
  drafts: "hide",
  due: null,
  sort: "num",
});

export const clearedFilters = (sort: Sort): Filters => ({ ...initialFilters(), lanes: new Set(), sort });

/** How many filters the "Filters" popover has on (the queue chips, the search and the sort sit outside it).
 *  `withProject`: false on a project's own board, where the Project filter isn't offered. */
export function activeFilterCount(f: Filters, withProject = true): number {
  return (
    [f.tag, f.role, f.teams, f.kind, f.due].filter((v) => v != null).length +
    (f.asg.length ? 1 : 0) +
    (f.notes !== "all" ? 1 : 0) +
    (f.nondev ? 1 : 0) +
    (f.notReviewed ? 1 : 0) +
    (f.high ? 1 : 0) +
    (withProject && f.project != null ? 1 : 0) +
    (withProject && f.noProject ? 1 : 0) +
    (withProject && f.hideProjects.length ? 1 : 0) +
    (f.drafts && f.drafts !== "hide" ? 1 : 0)
  );
}

/** The assignee choice as a list, from what an older saved or carried-over state may hold (one name, or null). */
export function asgList(v: unknown): string[] {
  if (Array.isArray(v)) return [...new Set(v.filter((x): x is string => typeof x === "string" && x.length > 0))];
  return typeof v === "string" && v ? [v] : [];
}

/** Filters to open with, from a partial (carried over by "Open as board", or restored): an old single assignee still works. */
export function withFilters(base: Filters, over: Partial<Filters> | undefined): Filters {
  if (!over) return base;
  const { asg, ...rest } = over as Partial<Filters> & { asg?: unknown };
  return { ...base, ...rest, ...(asg !== undefined ? { asg: asgList(asg) } : {}) };
}

/** Every filter in the popover off; the queues, the search and the sort stay as they are. */
export const withoutPopoverFilters = (f: Filters): Filters => ({
  ...f,
  tag: null,
  role: null,
  asg: [],
  teams: null,
  kind: null,
  notes: "all",
  nondev: false,
  notReviewed: false,
  high: false,
  project: null,
  noProject: false,
  hideProjects: [],
  drafts: "hide",
  due: null,
});

/** A card's project chip clicked: only that project's items (the Project filter), or everything again when it is on already. */
export const chipProject = (f: Filters, num: number): Filters => ({ ...f, project: f.project === num ? null : num, noProject: false, hideProjects: f.hideProjects.filter((n) => n !== num) });

/**
 * Whether an item shows under these filters. `today`: today on the board ("2026-10-15", its time zone),
 * for the Due filter; by default today in the board's default time zone.
 */
export function matches(it: BoardItem, f: Filters, allowNonDev = false, today: string = todayIn(), me: string | null = null, meId: string | null = null): boolean {
  // the same rules a saved view runs (core/src/views.ts): the queue as the chips count it (a fix with a PR
  // ready for review is in PR review), High, the assignee list ("@me" in a saved view is whoever opens it), ...
  return matchesFilters(it, f, { allowNonDev, today, me, meId });
}

// -- the queues in the URL: ?queue=review (a link straight to the PR review queue), ?queue=todo,review --

const LANE_IDS = new Set<string>(LANES.map((l) => l.id));

/** The queues a URL names (?queue=review, ?queue=todo,deploy; ?queue=all for every one), or null when it names none. */
export function readQueues(search: string): Set<Lane> | null {
  const v = new URLSearchParams(search).get("queue");
  if (v == null) return null;
  if (v.trim() === "all") return new Set();
  const ids = v.split(",").map((s) => s.trim()).filter((s): s is Lane => LANE_IDS.has(s));
  return ids.length ? new Set(ids) : null;
}

/** The query string with the queues in it (left out when they're the board's default), "" or "?...". */
export function writeQueues(search: string, lanes: Set<Lane> | null): string {
  const q = new URLSearchParams();
  for (const [k, v] of new URLSearchParams(search)) if (k !== "queue") q.append(k, v);
  const same = lanes && lanes.size === OPEN_LANES.length && OPEN_LANES.every((l) => lanes.has(l));
  if (lanes && !same) q.set("queue", lanes.size ? LANES.filter((l) => lanes.has(l.id)).map((l) => l.id).join(",") : "all");
  const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
  return s ? `?${s}` : "";
}

// -- the Due filter in the URL: ?due=overdue (a link straight to what's overdue) --

/** The Due filter a URL names (?due=overdue|week|later|none), or null. */
export function readDue(search: string): DueFilter | null {
  const v = new URLSearchParams(search).get("due");
  return isDueFilter(v) ? v : null;
}

/** The query string with the Due filter in it (or taken out: null), "" or "?...". */
export function writeDue(search: string, due: DueFilter | null): string {
  const q = new URLSearchParams();
  for (const [k, v] of new URLSearchParams(search)) if (k !== "due") q.append(k, v);
  if (due) q.set("due", due);
  const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
  return s ? `?${s}` : "";
}

// -- Drafts in the URL (OEM-286): ?drafts=show (everyone's), ?drafts=only --

/** The Drafts filter a URL names (?drafts=show|only), or null (the default: others' hidden). */
export function readDrafts(search: string): DraftsFilter | null {
  const v = new URLSearchParams(search).get("drafts");
  return isDraftsFilter(v) && v !== "hide" ? v : null;
}

/** The query string with the Drafts filter in it (left out while it's the default), "" or "?...". */
export function writeDrafts(search: string, drafts: DraftsFilter | null): string {
  const q = new URLSearchParams();
  for (const [k, v] of new URLSearchParams(search)) if (k !== "drafts") q.append(k, v);
  if (drafts && drafts !== "hide") q.set("drafts", drafts);
  const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
  return s ? `?${s}` : "";
}

// -- No project and Hide (OEM-260) in the URL (?noproject=1, ?hide=175,180) and per person on this device --

/** What the Project filter's No project and Hide say. */
export type ProjectView = Pick<Filters, "noProject" | "hideProjects">;

const hideList = (v: unknown): number[] =>
  Array.isArray(v) ? [...new Set(v.filter((n): n is number => Number.isInteger(n) && n > 0))].sort((a, b) => a - b).slice(0, 100) : [];

/** No project and Hide from a URL (?noproject=1, ?hide=175,180: projects' # numbers), or null when it names neither. */
export function readProjectView(search: string): ProjectView | null {
  const q = new URLSearchParams(search);
  const none = q.get("noproject");
  const hide = q.get("hide");
  if (none == null && hide == null) return null;
  return { noProject: none === "1", hideProjects: hideList((hide ?? "").split(",").map((s) => Number(s.trim()))) };
}

/** The query string with No project and Hide in it (left out while they're off), "" or "?...". */
export function writeProjectView(search: string, v: ProjectView | null): string {
  const q = new URLSearchParams();
  for (const [k, val] of new URLSearchParams(search)) if (k !== "noproject" && k !== "hide") q.append(k, val);
  if (v?.noProject) q.set("noproject", "1");
  if (v?.hideProjects.length) q.set("hide", v.hideProjects.join(","));
  const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
  return s ? `?${s}` : "";
}

const projectViewKey = (viewerId: string, archive: boolean) => `buyoem.projectView.${archive ? "archive." : ""}${viewerId}`;

/** The No project and Hide this person left the main board (or the archive) in, on this board and device. */
export function savedProjectView(viewerId: string | null | undefined, archive = false, org?: string): ProjectView {
  const none = { noProject: false, hideProjects: [] };
  if (!viewerId) return none;
  try {
    const raw = JSON.parse(readOrgSetting(projectViewKey(viewerId, archive), org) ?? "null") as { noProject?: unknown; hideProjects?: unknown } | null;
    if (!raw || typeof raw !== "object") return none;
    return { noProject: raw.noProject === true, hideProjects: hideList(raw.hideProjects) };
  } catch {
    return none;
  }
}

/** Remember No project and Hide (nothing set: forget them). */
export function saveProjectView(viewerId: string | null | undefined, v: ProjectView, archive = false, org?: string) {
  if (!viewerId) return;
  writeOrgSetting(projectViewKey(viewerId, archive), v.noProject || v.hideProjects.length ? JSON.stringify({ noProject: v.noProject, hideProjects: v.hideProjects }) : null, org);
}

/** Distinct people who reacted to or noted an item. */
export function reviewerCount(it: BoardItem) {
  return new Set([...it.reactions.map((r) => r.name), ...it.notes.map((n) => n.name)]).size;
}

const PW = { high: 2, normal: 1, low: 0 } as const;
const SW = { open: 0, closed: 1 } as const;

export function sortItems(list: BoardItem[], sort: Sort): BoardItem[] {
  const a = list.slice();
  if (sort === "newest") a.sort((x, y) => y.addedAt.localeCompare(x.addedAt) || y.num - x.num);
  else if (sort === "attention") a.sort((x, y) => reviewerCount(y) - reviewerCount(x) || x.num - y.num);
  else if (sort === "priority") a.sort((x, y) => PW[y.priority] - PW[x.priority] || SW[x.status] - SW[y.status] || x.num - y.num);
  // the soonest due first, items with no date last
  else if (sort === "due") a.sort((x, y) => compareDue(x.due, y.due) || x.num - y.num);
  else a.sort((x, y) => x.num - y.num);
  return a;
}
